import { useQuery } from "@tanstack/react-query";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, officeDataAvailable, isAllOffices, liveOffices, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import type { RiskTier } from "./types";
import type {
  DealerCreditQuality,
  DealerOfficeBreakdown,
  DealerQualitySummary,
  DealerVelocity,
} from "./types";

export type DealerQualityParams = {
  range?: string | undefined;
  office?: string | undefined;
  compare?: boolean | undefined;
};

// "Other" is ungraded, so it has no place on the A..B3 quality scale. Scored 0 — below B3 — so a
// dealer whose modal grade is ungraded never outranks one with a real grade.
export const TIER_SCORE: Record<RiskTier, number> = { A: 4, B1: 3, B2: 2, B3: 1, Other: 0 };

/**
 * Hand-tuned dealer seeds. Volume share is Pareto-ish (top 3 ≈ 56%) and the
 * tier/exception spread is deliberate: a clean high-tier cluster, a
 * low-tier/high-exception cluster, and enough middle ground to read as real.
 */
const DEALER_SEEDS: {
  dealerId: string;
  tier: RiskTier;
  sharePct: number;
  approvalRatePct: number;
  exceptionRatePct: number;
  overrideRatePct: number;
  avgTicketSize: number;
  firstHours: number;
  finalHours: number;
  officeWeights: number[];
}[] = [
  // Top of the Pareto curve.
  { dealerId: "DLR-004", tier: "A", sharePct: 22.4, approvalRatePct: 91.6, exceptionRatePct: 4.2, overrideRatePct: 2.1, avgTicketSize: 148_500, firstHours: 3.4, finalHours: 6.1, officeWeights: [0.52, 0.28, 0.14, 0.04, 0.02] },
  { dealerId: "DLR-001", tier: "B1", sharePct: 19.1, approvalRatePct: 84.3, exceptionRatePct: 7.8, overrideRatePct: 3.6, avgTicketSize: 96_200, firstHours: 4.2, finalHours: 8.4, officeWeights: [0.44, 0.06, 0.34, 0.12, 0.04] },
  { dealerId: "DLR-007", tier: "B3", sharePct: 14.6, approvalRatePct: 63.8, exceptionRatePct: 24.6, overrideRatePct: 11.4, avgTicketSize: 61_400, firstHours: 7.9, finalHours: 19.2, officeWeights: [0.38, 0.31, 0.09, 0.18, 0.04] },
  // Mid pack.
  { dealerId: "DLR-002", tier: "B1", sharePct: 9.2, approvalRatePct: 88.1, exceptionRatePct: 6.4, overrideRatePct: 2.8, avgTicketSize: 112_800, firstHours: 3.9, finalHours: 7.3, officeWeights: [0.29, 0.41, 0.2, 0.06, 0.04] },
  { dealerId: "DLR-009", tier: "B2", sharePct: 8.0, approvalRatePct: 74.5, exceptionRatePct: 17.9, overrideRatePct: 8.2, avgTicketSize: 54_900, firstHours: 6.6, finalHours: 15.8, officeWeights: [0.22, 0.12, 0.48, 0.14, 0.04] },
  { dealerId: "DLR-005", tier: "A", sharePct: 6.4, approvalRatePct: 89.9, exceptionRatePct: 8.6, overrideRatePct: 4.4, avgTicketSize: 176_300, firstHours: 4.6, finalHours: 9.1, officeWeights: [0.34, 0.09, 0.11, 0.42, 0.04] },
  { dealerId: "DLR-011", tier: "B2", sharePct: 5.1, approvalRatePct: 78.2, exceptionRatePct: 12.7, overrideRatePct: 6.1, avgTicketSize: 68_700, firstHours: 5.4, finalHours: 12.4, officeWeights: [0.18, 0.36, 0.08, 0.1, 0.28] },
  { dealerId: "DLR-003", tier: "B1", sharePct: 4.4, approvalRatePct: 81.7, exceptionRatePct: 14.1, overrideRatePct: 7.3, avgTicketSize: 88_100, firstHours: 5.9, finalHours: 13.6, officeWeights: [0.41, 0.18, 0.27, 0.1, 0.04] },
  // Long tail.
  { dealerId: "DLR-012", tier: "B3", sharePct: 3.6, approvalRatePct: 67.4, exceptionRatePct: 27.3, overrideRatePct: 13.1, avgTicketSize: 43_200, firstHours: 8.8, finalHours: 21.7, officeWeights: [0.26, 0.06, 0.14, 0.08, 0.46] },
  { dealerId: "DLR-006", tier: "B2", sharePct: 3.1, approvalRatePct: 71.2, exceptionRatePct: 9.8, overrideRatePct: 5.2, avgTicketSize: 59_600, firstHours: 5.1, finalHours: 11.2, officeWeights: [0.31, 0.44, 0.13, 0.08, 0.04] },
  { dealerId: "DLR-008", tier: "A", sharePct: 2.4, approvalRatePct: 86.8, exceptionRatePct: 11.4, overrideRatePct: 5.8, avgTicketSize: 133_900, firstHours: 4.8, finalHours: 10.4, officeWeights: [0.48, 0.11, 0.19, 0.18, 0.04] },
  { dealerId: "DLR-010", tier: "B3", sharePct: 1.7, approvalRatePct: 60.9, exceptionRatePct: 19.6, overrideRatePct: 9.7, avgTicketSize: 38_400, firstHours: 7.2, finalHours: 17.1, officeWeights: [0.21, 0.24, 0.31, 0.2, 0.04] },
];

const TOTAL_APPLICATIONS = 1_240;

function round(value: number, decimals = 1) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function generateDealerQualitySummary(
  offices: OfficeMapping[],
  office?: string,
): DealerQualitySummary {
  const officeNames = offices.map((entry) => entry.officeName);
  const allOffices = isAllOffices(office);
  const velocityAvailable = officeDataAvailable(offices, office);

  const officeBreakdownByDealer: Record<string, DealerOfficeBreakdown[]> = {};

  const creditQuality: DealerCreditQuality[] = DEALER_SEEDS.map((seed) => {
    const dealerVolume = Math.max(6, Math.round((seed.sharePct / 100) * TOTAL_APPLICATIONS));

    const breakdown: DealerOfficeBreakdown[] = officeNames.map((officeName, index) => {
      const weight = seed.officeWeights[index] ?? 0;
      const entry = offices[index];
      const isLive = Boolean(entry?.liveOnAthena);
      // Approval rate drifts slightly per office; exception rate only exists post go-live.
      const drift = ((index % 3) - 1) * 2.4;
      return {
        officeName,
        volumeCount: Math.round(dealerVolume * weight),
        approvalRatePct: round(Math.min(96, Math.max(52, seed.approvalRatePct + drift))),
        exceptionRatePct: isLive ? round(seed.exceptionRatePct) : null,
      };
    });
    officeBreakdownByDealer[seed.dealerId] = breakdown;

    // Office is an additional cut: keep the dealer, reshape its volume.
    const officeWeight = allOffices
      ? 1
      : (seed.officeWeights[officeNames.indexOf(office as string)] ?? 0);
    const scopedVolume = Math.max(1, Math.round(dealerVolume * officeWeight));

    return {
      dealerId: seed.dealerId,
      modeRiskTier: seed.tier,
      riskTierScore: TIER_SCORE[seed.tier],
      approvalRatePct: round(seed.approvalRatePct),
      volumeCount: scopedVolume,
      volumeSharePct: 0, // normalized below against the scoped total
    };
  });

  const scopedTotal = creditQuality.reduce((sum, row) => sum + row.volumeCount, 0) || 1;
  for (const row of creditQuality) {
    row.volumeSharePct = round((row.volumeCount / scopedTotal) * 100);
  }

  const rows: DealerVelocity[] = velocityAvailable
    ? DEALER_SEEDS.map((seed) => ({
        dealerId: seed.dealerId,
        medianFirstDecisionHours: round(seed.firstHours),
        medianFinalDecisionHours: round(seed.finalHours),
        exceptionRatePct: round(seed.exceptionRatePct),
        overrideRatePct: round(seed.overrideRatePct),
        avgTicketSize: seed.avgTicketSize,
      }))
    : [];

  return {
    creditQuality,
    velocity: { dataAvailable: velocityAvailable, rows },
    earlyDelinquency: {
      available: false,
      note: "Requires 6-12 months of loan aging after go-live — not yet available. This will be the strongest signal on this page once it's ready.",
    },
    officeBreakdownByDealer,
  };
}

export async function fetchDealerQualitySummary(
  offices: OfficeMapping[],
  params: DealerQualityParams = {},
): Promise<DealerQualitySummary> {
  if (mockFor("/dealer-quality")) return mockResponse(generateDealerQualitySummary(offices, params.office));
  return apiGet<DealerQualitySummary>("/dealer-quality", {
    range: params.range,
    office: params.office,
    compare: params.compare ? "true" : undefined,
  });
}

export function useDealerQualitySummary(
  params: DealerQualityParams = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("dealer-quality", { ...params, offices: officesFingerprint(offices) }),
    queryFn: () => fetchDealerQualitySummary(offices ?? [], params),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}
